import { faFeatherPointed, faFloppyDisk, faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ToolTip from "components/General/ToolTip"
import { useCallback, useEffect, useRef, useState } from "react"
import { Button, Overlay, Popover } from "react-bootstrap"
import { toast } from "react-toastify"
import { saveNotes } from "shared/routes/student"
import { Type } from "shared/types/moduleTypes"

interface Props {
    prevNotes?: string
    mode: "read" | "write"
    disableSaving: boolean
    data: {
        submissionId: string
        moduleName: string
        type: Type
        assessmentNumber: number
        assessmentId: string
        questionNumber: number
    } | null
}

const StudentNotes = ({ data, prevNotes, mode, disableSaving }: Props) => {
    const [showNotes, setShowNotes] = useState(false)
    const popoverRef = useRef<HTMLDivElement>(null)
    const target = useRef(null)
    const [notes, setNotes] = useState("")

    useEffect(() => {
        if (prevNotes) setNotes(prevNotes)
    }, [prevNotes])

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target as Node) && target.current && !target.current.contains(event.target as Node)) {
            setShowNotes(false)
        }
    }, [])

    const save = async () => {
        if (!data || disableSaving) {
            setShowNotes(false)
            return
        }
        try {
            await saveNotes({ 
                moduleName: data.moduleName, 
                type: data.type, 
                assessmentNumber: data.assessmentNumber, 
                assessmentId: data.assessmentId, 
                submissionId: data.submissionId, 
                questionNumber: data.questionNumber, 
                notes: notes, 
            })
            toast.success("Note saved.", { autoClose: 1000, })
        } catch (error) {
            toast.error(error?.response?.data?.message ?? "Error saving notes. Please contact STEM Sims if the issue persists.")
        } finally {
            setShowNotes(false)
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [handleClickOutside])

  return (
    <div className="w-100 d-flex justify-content-end mb-1">
        <ToolTip title="Notes for this question">
            <FontAwesomeIcon 
                role="button" 
                ref={target} 
                className={notes ? "text-success" : "text-primary"}
                icon={faFeatherPointed} 
                size="2x" 
                onClick={() => setShowNotes(!showNotes)} />
        </ToolTip>
        <Overlay
            ref={popoverRef}
            show={showNotes}
            target={target.current}
            placement="left">
            <Popover style={{ maxWidth: "none", width: "450px" }}>
                <Popover.Header as="h3" className="bg-info fw-bold d-flex justify-content-between">
                    <span><FontAwesomeIcon icon={faFeatherPointed} /> Leave notes</span>
                    <FontAwesomeIcon icon={faXmark} size={"lg"} role="button" onClick={() => setShowNotes(false)} />
                </Popover.Header>
                <Popover.Body className="d-flex flex-column">
                    <textarea 
                        disabled={mode === "read"}
                        className="form-control w-100" 
                        rows={10} 
                        placeholder={mode === "write" ? "Enter notes here": "Empty Notes"} 
                        value={notes} 
                        onChange={(e) => setNotes(e.target.value)}>
                    </textarea>
                    {mode === "write" && 
                        <Button 
                            variant="primary" 
                            className="mt-2" 
                            onClick={save}>
                               <FontAwesomeIcon icon={faFloppyDisk} /> Save Notes
                        </Button>}
                </Popover.Body>
            </Popover>
        </Overlay>
    </div>
  )
}

export default StudentNotes
